import {Link, useParams} from "react-router-dom";
import React, {useEffect} from "react";
import {
    removeTraveler,
    addTraveler,
    addBundleMember,
    removeBundleMember,
    setPricingModel
} from "../actions/travelers";
import {useDispatch, useSelector} from "react-redux";
import {setCurrentTrain} from "../actions/trains";
import {isFlexibleBundle, maxForVariableMember} from "../reducers/travelers";
import NumberStepper from "./NumberStepper";

export default function Travelers() {
    const { id } = useParams();
    const dispatch = useDispatch();
    const travelers = useSelector(state => state.travelers);
    const { currentTrain: train } = useSelector(state => state.trains);

    const checkSeatLimit = event => {
        if (travelers.total > train.totalAvailableSeats || travelers.total === 0) {
            event.preventDefault()
        }
    };

    useEffect(() => {
        dispatch(setCurrentTrain(id));
    }, []);

    // Initialize pricing model when train loads
    useEffect(() => {
        if (train && train.pricingModel) {
            dispatch(setPricingModel(train.pricingModel));
        }
    }, [train, dispatch]);

    const categories = travelers.categories || {};

    return (
        <>
            {train && (
            <div>
                <div className="flow-root mt-6">
                    <h3 className="text-center text-base font-semibold text-indigo-600 tracking-wide uppercase">Antal resenärer</h3>
                    <ul className="-my-5 divide-y divide-gray-200">
                        {Object.keys(categories).map((categoryKey) => (
                            <React.Fragment key={categoryKey}>
                                {!train.isFullPriceOnly && (
                                    <li className="py-4">
                                        <div className="flex items-center space-x-4">
                                            <div className="flex-1 min-w-0">
                                                <p id={`traveler-${categoryKey}-name`} className="text-sm font-medium text-gray-900 truncate">{categories[categoryKey].name}</p>
                                                <p id={`traveler-${categoryKey}-description`} className="text-sm text-gray-500 truncate">{categories[categoryKey].description}</p>
                                            </div>
                                            <NumberStepper
                                                value={categories[categoryKey].count}
                                                label={`${categories[categoryKey].name} ${categories[categoryKey].description}`}
                                                labelledBy={`traveler-${categoryKey}-name traveler-${categoryKey}-description`}
                                                max={isFlexibleBundle(categories[categoryKey]) ? 1 : undefined}
                                                onIncrement={() => dispatch(addTraveler(categoryKey))}
                                                onDecrement={() => dispatch(removeTraveler(categoryKey))}
                                            />
                                        </div>
                                        {/* Variable bundle members, e.g. number of children in a family ticket. */}
                                        {categories[categoryKey].composition && categories[categoryKey].count > 0 &&
                                            categories[categoryKey].composition
                                                .filter((entry) => entry.count == null)
                                                .map((entry) => {
                                                    const member = categories[entry.type] || {};
                                                    const value = categories[categoryKey].variableCounts?.[entry.type] ?? 0;
                                                    const nameId = `traveler-${categoryKey}-${entry.type}-name`;
                                                    const descriptionId = `traveler-${categoryKey}-${entry.type}-description`;
                                                    return (
                                                        <div key={entry.type} className="mt-4 flex items-center space-x-4 pl-4 border-l-2 border-gray-100">
                                                            <div className="flex-1 min-w-0">
                                                                <p id={nameId} className="text-sm font-medium text-gray-900 truncate">{member.name} i {categories[categoryKey].name.toLowerCase()}</p>
                                                                <p id={descriptionId} className="text-sm text-gray-500 truncate">{member.description}</p>
                                                            </div>
                                                            <NumberStepper
                                                                value={value}
                                                                label={`${member.name} ${member.description ?? ''} i ${categories[categoryKey].name.toLowerCase()}`.replace(/\s+/g, ' ')}
                                                                labelledBy={`${nameId} ${descriptionId}`}
                                                                min={entry.min ?? 0}
                                                                max={maxForVariableMember(categories[categoryKey], entry)}
                                                                onIncrement={() => dispatch(addBundleMember(categoryKey, entry.type))}
                                                                onDecrement={() => dispatch(removeBundleMember(categoryKey, entry.type))}
                                                            />
                                                        </div>
                                                    );
                                                })}
                                    </li>
                                )}
                                {train.isFullPriceOnly && categoryKey === 'adult' && (
                                    <li className="py-4">
                                        <div className="flex items-center space-x-4">
                                            <div className="flex-1 min-w-0">
                                                <p id="traveler-full-price-name" className="text-sm font-medium text-gray-900 truncate">Resenär</p>
                                                <p id="traveler-full-price-description" className="text-sm text-gray-500 truncate">Vuxen/Ungdom/Barn</p>
                                            </div>
                                            <NumberStepper
                                                value={categories[categoryKey].count}
                                                label="Resenär Vuxen/Ungdom/Barn"
                                                labelledBy="traveler-full-price-name traveler-full-price-description"
                                                onIncrement={() => dispatch(addTraveler(categoryKey))}
                                                onDecrement={() => dispatch(removeTraveler(categoryKey))}
                                            />
                                        </div>
                                    </li>
                                )}
                            </React.Fragment>
                        ))}
                    </ul>
                </div>
                {travelers.total > train.totalAvailableSeats && (<div className="mt-6 justify-center items-center">
                <span className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md bg-red-100 text-red-800">
                    Vi har inte så här många platser lediga på det här tåget. Kontakta oss för mer information.
                </span>
                </div>)}
                <div className="mt-6">
                    {travelers.total <= train.totalAvailableSeats && travelers.total > 0 && (
                        <Link to={"/trains/" + id + '/seats'}
                              onClick={checkSeatLimit}
                              className="w-full flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                            Fortsätt
                        </Link>
                    )}
                    {! travelers.total <= train.totalAvailableSeats && travelers.total <= 0 && (
                        <div className="w-full flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                            Fortsätt
                        </div>
                    )}
                </div>
            </div>
            )}
        </>
    )
}
